import { useCallback, useRef, useState } from 'react';
import { parseAPIError, ParsedError } from './error-utils';

const NEWLINE = '\n'.charCodeAt(0);

// Stream part type definitions
interface StreamPartBase {
  code: string;
  name: string;
  parse: (value: any) => StreamPart;
}

interface TextStreamPart {
  type: 'text';
  value: string;
}

interface JsonStreamPart {
  type: 'json';
  value: string;
}

interface CodeStreamPart {
  type: 'code';
  value: string;
}

type StreamPart = TextStreamPart | JsonStreamPart | CodeStreamPart;

const textStreamPart: StreamPartBase = {
  code: '0',
  name: 'text',
  parse: (value: any): TextStreamPart => {
    if (typeof value !== 'string') {
      throw new Error('"text" parts expect a string value.');
    }
    return { type: 'text', value };
  },
};

const jsonStreamPart: StreamPartBase = {
  code: '1',
  name: 'json',
  parse: (value: any): JsonStreamPart => {
    if (typeof value !== 'string') {
      throw new Error('"json" parts expect a string value.');
    }
    return { type: 'json', value };
  },
};

const codeStreamPart: StreamPartBase = {
  code: '2',
  name: 'code',
  parse: (value: any): CodeStreamPart => {
    if (typeof value !== 'string') {
      throw new Error('"code" parts expect a string value.');
    }
    return { type: 'code', value };
  },
};

const endStreamPart: StreamPartBase = {
  code: 'e',
  name: 'text',
  parse: (): TextStreamPart => ({ type: 'text', value: '' }),
};

const stopStreamPart: StreamPartBase = {
  code: 'd',
  name: 'text',
  parse: (): TextStreamPart => ({ type: 'text', value: '' }),
};

const streamParts: StreamPartBase[] = [
  textStreamPart,
  jsonStreamPart,
  codeStreamPart,
  endStreamPart,
  stopStreamPart,
];

export const streamPartsByCode: Record<string, StreamPartBase> = {
  [textStreamPart.code]: textStreamPart,
  [jsonStreamPart.code]: jsonStreamPart,
  [codeStreamPart.code]: codeStreamPart,
  [endStreamPart.code]: endStreamPart,
  [stopStreamPart.code]: stopStreamPart,
};

export const validCodes: string[] = streamParts.map((part) => part.code);

// Concatenates all the chunks into a single Uint8Array
function concatChunks(chunks: Uint8Array[], totalLength: number): Uint8Array {
  const concatenatedChunks = new Uint8Array(totalLength);

  let offset = 0;
  for (const chunk of chunks) {
    concatenatedChunks.set(chunk, offset);
    offset += chunk.length;
  }
  chunks.length = 0;

  return concatenatedChunks;
}

interface ReadDataStreamOptions {
  isAborted?: () => boolean;
}

export async function* readDataStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  { isAborted }: ReadDataStreamOptions = {}
): AsyncGenerator<StreamPart> {
  const decoder = new TextDecoder();
  const chunks: Uint8Array[] = [];
  let totalLength = 0;

  while (true) {
    const { value } = await reader.read();

    if (value) {
      chunks.push(value);
      totalLength += value.length;
      if (value[value.length - 1] !== NEWLINE) {
        // If the last character is not a newline, we have not read the whole JSON value
        continue;
      }
    }

    if (chunks.length === 0) {
      break; // We have reached the end of the stream
    }

    const concatenatedChunks = concatChunks(chunks, totalLength);
    totalLength = 0;

    const streamParts = decoder
      .decode(concatenatedChunks, { stream: true })
      .split('\n')
      .filter((line) => line !== '')
      .map((line) => {
        const firstSeparatorIndex = line.indexOf(':');

        if (firstSeparatorIndex === -1) {
          throw new Error('Failed to parse stream string. No separator found.');
        }

        const prefix = line.slice(0, firstSeparatorIndex);

        if (!validCodes.includes(prefix)) {
          throw new Error(`Failed to parse stream string. Invalid code ${prefix}.`);
        }

        const code = prefix;
        const textValue = line.slice(firstSeparatorIndex + 1);
        const jsonValue = JSON.parse(textValue);

        return streamPartsByCode[code].parse(jsonValue);
      });

    for (const streamPart of streamParts) {
      yield streamPart;
    }

    // The request has been aborted, stop reading the stream.
    if (isAborted?.()) {
      reader.cancel();
      break;
    }
  }
}

// Message types
interface Message {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp?: string;
}

interface UseChatOptions {
  api: string;
  onResponse?: (response: string) => void;
  onError?: (error: ParsedError) => void;
  body?: Record<string, any>;
  initialMessages?: Message[];
  onFinish?: (message: Message) => void;
}

interface SubmitParams {
  input?: string;
  messages?: Message[];
}

interface UseChatReturn {
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  input: string;
  setInput: (input: string) => void;
  handleSubmit: (e: React.FormEvent, params?: SubmitParams) => void;
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  isLoading: boolean;
  isStreaming: boolean;
  append: (message: Message) => void;
  stop: () => void;
  error: ParsedError | null;
  errorMessage: string | null;
}

export function useChat({
  api,
  onResponse,
  onError,
  body,
  initialMessages = [],
  onFinish,
}: UseChatOptions): UseChatReturn {
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [input, setInput] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<ParsedError | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const stop = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setIsLoading(false);
      setIsStreaming(false);
    }
  }, []);

  // Handler to trigger stream reading and processing
  const handleStreamResponse = useCallback(
    async (response: Response) => {
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('Response body is not readable');
      }

      const id = Date.now().toString();
      const streamGenerator = readDataStream(reader, {
        isAborted: () => abortControllerRef.current?.signal.aborted ?? false,
      });
      let chunks = '';
      let finalMessage: Message | null = null;

      try {
        setIsStreaming(true); // Start streaming
        for await (const part of streamGenerator) {
          switch (part.type) {
            case 'text':
              chunks += part.value;
              setMessages((prevMessages) => {
                const newMessages = [...prevMessages];
                let msg = newMessages.find((m) => m.id === id);
                if (msg) {
                  msg.content += part.value;
                } else {
                  msg = {
                    id,
                    content: part.value,
                    role: 'assistant',
                    timestamp: new Date().toISOString(),
                  };
                  newMessages.push(msg);
                }
                finalMessage = msg;
                return newMessages;
              });
              break;
          }
        }

        if (onResponse) {
          onResponse(chunks);
        }

        if (onFinish && finalMessage) {
          onFinish(finalMessage);
        }
      } catch (e) {
        // Handle abort
        if (e instanceof Error && e.name === 'AbortError') {
          return;
        }

        const parsedError = parseAPIError(e);
        onError?.(parsedError);
        setError(parsedError);
        setErrorMessage(parsedError.message);
      } finally {
        setIsLoading(false);
        setIsStreaming(false);
      }
    },
    [onResponse, onError, onFinish]
  );

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    setInput(e.target.value);
  };

  const append = useCallback(
    (message: Message) => {
      const newMessages = [...messages, message];
      setMessages(newMessages);
      setIsLoading(true);

      // Create new abort controller
      abortControllerRef.current = new AbortController();

      const requestBody = { ...body, messages: newMessages, prompt: message };

      fetch(api, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        signal: abortControllerRef.current.signal,
      })
        .then(async (res) => {
          if (res.ok) {
            await handleStreamResponse(res);
          } else {
            const errorData = await res.json().catch(() => ({ message: 'Failed to fetch' }));
            const parsedError = parseAPIError(errorData);
            onError?.(parsedError);
            setError(parsedError);
            setErrorMessage(parsedError.message);
            setIsLoading(false);
            setIsStreaming(false);
          }
        })
        .catch((err) => {
          // Handle abort
          if (err.name === 'AbortError') {
            return;
          }

          const parsedError = parseAPIError(err);
          onError?.(parsedError);
          setError(parsedError);
          setErrorMessage(parsedError.message);
          setIsLoading(false);
          setIsStreaming(false);
        })
        .finally(() => {
          abortControllerRef.current = null;
        });
    },
    [api, body, messages, handleStreamResponse, onError]
  );

  const handleSubmit = useCallback(
    (e: React.FormEvent, params?: SubmitParams): void => {
      e.preventDefault();
      if (!input && !params?.input) {
        return;
      }

      const messageContent = params?.input || input;
      const newMessage: Message = {
        id: Date.now().toString(),
        content: messageContent,
        role: 'user',
        timestamp: new Date().toISOString(),
      };
      const newMessages: Message[] = messages.concat(newMessage);

      setMessages(newMessages);
      setInput('');
      setIsLoading(true);

      // Create new abort controller
      abortControllerRef.current = new AbortController();

      const requestBody = {
        ...body,
        messages: newMessages,
      };

      fetch(api, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
        signal: abortControllerRef.current.signal,
      })
        .then(async (res) => {
          if (res.ok) {
            await handleStreamResponse(res);
          } else {
            const errorData = await res.json().catch(() => ({ message: 'Failed to fetch' }));
            const parsedError = parseAPIError(errorData);
            onError?.(parsedError);
            setError(parsedError);
            setErrorMessage(parsedError.message);
            setIsLoading(false);
            setIsStreaming(false);
          }
        })
        .catch((err) => {
          // Handle abort
          if (err.name === 'AbortError') {
            return;
          }

          const parsedError = parseAPIError(err);
          setError(parsedError);
          setErrorMessage(parsedError.message);
          onError?.(parsedError);
          setIsLoading(false);
          setIsStreaming(false);
        })
        .finally(() => {
          abortControllerRef.current = null;
        });
    },
    [body, input, messages, api, handleStreamResponse, onError]
  );

  return {
    messages,
    setMessages,
    input,
    setInput,
    handleSubmit,
    handleInputChange,
    isLoading,
    isStreaming,
    append,
    stop,
    error,
    errorMessage,
  };
}
